'use client';

import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { io, type Socket } from 'socket.io-client';
import {
  SessionCredentialsSchema,
  type MarkerColor,
  type MarkerKind,
  type ClientToServerEvents,
  type PublicRoomSummary,
  type RoomView,
  type ServerToClientEvents,
  type SessionCredentials,
  type SessionOutput,
} from '@concept/protocol';
import { GameBoard, type MarkerDraftChanges } from '../features/game/game-board';
import { createCommandId } from '../features/game/command-id';
import { QrJoinScanner } from '../features/join/qr-join-scanner';
import { parseRoomInvite } from '../features/join/room-invite';
import { RoomInviteQr } from '../features/join/room-invite-qr';
import { InstallApp } from '../features/pwa/install-app';
import { useViewportHeight } from '../hooks/use-viewport-height';
import { useBoardImagePrefetch } from '../hooks/use-board-image-prefetch';

const SESSION_KEY = 'concept-room-session';
const NICKNAME_KEY = 'concept-last-nickname';
const CONNECTION_ALERT_DELAY_MS = 8_000;
type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export default function HomePage() {
  const viewportHeight = useViewportHeight();
  const socketRef = useRef<GameSocket | null>(null);
  const [room, setRoom] = useState<RoomView | null>(null);
  const [credentials, setCredentials] = useState<SessionCredentials | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [showConnectionAlert, setShowConnectionAlert] = useState(false);
  const [nickname, setNickname] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [mode, setMode] = useState<'create' | 'join'>('create');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [isBoardVisible, setIsBoardVisible] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [publicRooms, setPublicRooms] = useState<PublicRoomSummary[]>([]);

  useBoardImagePrefetch(isConnected && room?.phase !== 'freestyle');

  useEffect(() => {
    const savedNickname = readSavedNickname();
    if (savedNickname) setNickname(savedNickname);
  }, []);

  useEffect(() => {
    const code = parseRoomInvite(window.location.href);
    if (!code) return;
    setMode('join');
    setRoomCode(code);
    window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash}`);
  }, []);

  useEffect(() => {
    const socket: GameSocket = io({ autoConnect: false });
    let disposed = false;
    let connectionAlertTimer: number | null = null;
    socketRef.current = socket;

    const clearConnectionAlert = () => {
      if (connectionAlertTimer !== null) {
        window.clearTimeout(connectionAlertTimer);
        connectionAlertTimer = null;
      }
      setShowConnectionAlert(false);
    };

    const scheduleConnectionAlert = () => {
      if (disposed || connectionAlertTimer !== null) return;
      connectionAlertTimer = window.setTimeout(() => {
        connectionAlertTimer = null;
        if (!disposed && !socket.connected) setShowConnectionAlert(true);
      }, CONNECTION_ALERT_DELAY_MS);
    };

    socket.on('connect', () => {
      setIsConnected(true);
      clearConnectionAlert();
      socket.emit('room:list-public', {}, (result) => {
        if (result.ok) setPublicRooms(result.value.rooms);
      });
      const saved = readSavedSession();
      if (!saved) return;
      try {
        const parsed = SessionCredentialsSchema.safeParse(JSON.parse(saved));
        if (!parsed.success) {
          clearSavedSession();
          return;
        }
        socket.emit('room:resume', parsed.data, (result) => {
          if (result.ok) {
            acceptSession(result.value);
          } else {
            clearSavedSession();
          }
        });
      } catch {
        clearSavedSession();
      }
    });
    socket.on('disconnect', () => {
      setIsConnected(false);
      scheduleConnectionAlert();
    });
    socket.on('connect_error', () => {
      setIsConnected(false);
      scheduleConnectionAlert();
    });
    socket.on('room:view', setRoom);
    socket.on('room:public-list', setPublicRooms);
    socket.connect();
    scheduleConnectionAlert();

    return () => {
      disposed = true;
      if (connectionAlertTimer !== null) window.clearTimeout(connectionAlertTimer);
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  function acceptSession(session: SessionOutput) {
    setCredentials(session.credentials);
    setRoom(session.room);
    setError('');
    saveSession(session.credentials);
  }

  function rememberNicknameFromSession(session: SessionOutput) {
    const player = session.room.players.find(
      (candidate) => candidate.id === session.credentials.playerId,
    );
    if (!player) return;
    setNickname(player.nickname);
    saveNickname(player.nickname);
  }

  function leaveRoom() {
    const socket = socketRef.current;
    if (!socket?.connected || !credentials) return;
    setBusy(true);
    socket.emit('room:leave', {}, (result) => {
      setBusy(false);
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      clearSavedSession();
      setCredentials(null);
      setRoom(null);
      setError('');
      setMode('create');
      setRoomCode('');
      setIsBoardVisible(true);
    });
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const socket = socketRef.current;
    if (!socket?.connected) {
      setError('The game server is not connected yet.');
      return;
    }

    setBusy(true);
    setError('');
    const done = (result: Parameters<Parameters<ClientToServerEvents['room:create']>[1]>[0]) => {
      setBusy(false);
      if (result.ok) {
        acceptSession(result.value);
        rememberNicknameFromSession(result.value);
      } else setError(result.error.message);
    };

    if (mode === 'create') {
      socket.emit('room:create', { nickname, isPublic }, done);
    } else {
      socket.emit('room:join', { nickname, roomCode }, done);
    }
  }

  function changeNickname() {
    const socket = socketRef.current;
    if (!socket || !credentials || !room) return;
    const me = room.players.find((player) => player.id === credentials.playerId);
    if (!me) return;
    const nextNickname = window.prompt('Choose your nickname', me.nickname)?.trim();
    if (!nextNickname || nextNickname === me.nickname) return;
    socket.emit('player:update-nickname', {
      roomCode: credentials.roomCode,
      commandId: createCommandId(),
      expectedVersion: room.version,
      nickname: nextNickname,
    }, (result) => {
      acceptRoomCommand(result);
      if (result.ok) {
        const updatedNickname = result.value.room.players.find(
          (player) => player.id === credentials.playerId,
        )?.nickname;
        if (updatedNickname) {
          setNickname(updatedNickname);
          saveNickname(updatedNickname);
        }
      }
      if (!result.ok && room.phase === 'freestyle') {
        window.alert(result.error.message);
      }
    });
  }

  function openBoard() {
    if (!credentials || !room) return;
    if (room.phase === 'freestyle') {
      setIsBoardVisible(true);
      return;
    }
    const socket = socketRef.current;
    if (!socket) return;
    setBusy(true);
    socket.emit('lobby:open-board', {
      roomCode: credentials.roomCode,
      commandId: createCommandId(),
      expectedVersion: room.version,
    }, (result) => {
      setBusy(false);
      if (result.ok) {
        setRoom(result.value.room);
        setIsBoardVisible(true);
      }
      else setError(result.error.message);
    });
  }

  function toggleClueGiving() {
    const socket = socketRef.current;
    if (!socket || !credentials || !room) return;
    setBusy(true);
    socket.emit('freestyle:toggle-clue-giver', {
      roomCode: credentials.roomCode,
      commandId: createCommandId(),
      expectedVersion: room.version,
    }, (result) => {
      setBusy(false);
      if (result.ok) setRoom(result.value.room);
      else setError(result.error.message);
    });
  }

  function placeMarker(iconId: string, color: MarkerColor, kind: MarkerKind) {
    const socket = socketRef.current;
    if (!socket || !credentials || !room) return;
    socket.emit('board:place-marker', {
      roomCode: credentials.roomCode,
      commandId: createCommandId(),
      expectedVersion: room.version,
      iconId,
      color,
      kind,
    }, acceptRoomCommand);
  }

  function removeMarker(markerId: string) {
    const socket = socketRef.current;
    if (!socket || !credentials || !room) return;
    socket.emit('board:remove-marker', {
      roomCode: credentials.roomCode,
      commandId: createCommandId(),
      expectedVersion: room.version,
      markerId,
    }, acceptRoomCommand);
  }

  function moveMarker(markerId: string, iconId: string) {
    const socket = socketRef.current;
    if (!socket || !credentials || !room) return;
    socket.emit('board:move-marker', {
      roomCode: credentials.roomCode,
      commandId: createCommandId(),
      expectedVersion: room.version,
      markerId,
      iconId,
    }, acceptRoomCommand);
  }

  async function applyMarkerChanges(changes: MarkerDraftChanges) {
    const socket = socketRef.current;
    if (!socket || !credentials || !room) return;
    const activeSocket = socket;
    const roomCode = credentials.roomCode;
    let expectedVersion = room.version;

    async function runCommand(
      event: 'board:remove-marker' | 'board:place-marker',
      payload: { markerId: string } | { iconId: string; color: MarkerColor; kind: MarkerKind },
    ): Promise<boolean> {
      return new Promise((resolve) => {
        const input = {
          roomCode,
          commandId: createCommandId(),
          expectedVersion,
          ...payload,
        };
        const acknowledge = (result: {
          ok: boolean;
          value?: { room: RoomView };
          error?: { message: string };
        }) => {
          if (result.ok && result.value) {
            expectedVersion = result.value.room.version;
            setRoom(result.value.room);
            setError('');
            resolve(true);
          } else {
            if (result.error) setError(result.error.message);
            resolve(false);
          }
        };
        if (event === 'board:remove-marker' && 'markerId' in payload) {
          activeSocket.emit(event, input as Parameters<ClientToServerEvents[typeof event]>[0], acknowledge);
        } else if (event === 'board:place-marker' && 'iconId' in payload) {
          activeSocket.emit(event, input as Parameters<ClientToServerEvents[typeof event]>[0], acknowledge);
        }
      });
    }

    for (const markerId of changes.removedMarkerIds) {
      if (!await runCommand('board:remove-marker', { markerId })) return;
    }
    for (const addition of changes.additions) {
      if (!await runCommand('board:place-marker', addition)) return;
    }
  }

  function clearBoard() {
    const socket = socketRef.current;
    if (!socket || !credentials || !room) return;
    socket.emit('board:clear', {
      roomCode: credentials.roomCode,
      commandId: createCommandId(),
      expectedVersion: room.version,
    }, acceptRoomCommand);
  }

  function acceptRoomCommand(result: {
    ok: boolean;
    value?: { room: RoomView };
    error?: { message: string };
  }) {
    if (result.ok && result.value) {
      setRoom(result.value.room);
      setError('');
    } else if (result.error) {
      setError(result.error.message);
    }
  }

  const acceptScannedRoomCode = useCallback((code: string) => {
    setMode('join');
    setRoomCode(code);
    setError('');
    setShowQrScanner(false);
  }, []);

  if (room && credentials) {
    if (room.phase === 'freestyle' && isBoardVisible) {
      return (
        <>
          <GameBoard
            currentPlayerId={credentials.playerId}
            onApplyMarkerChanges={applyMarkerChanges}
            onChangeNickname={changeNickname}
            onClearBoard={clearBoard}
            onLeaveRoom={() => setIsBoardVisible(false)}
            onMoveMarker={moveMarker}
            onPlaceMarker={placeMarker}
            onRemoveMarker={removeMarker}
            onToggleClueGiver={toggleClueGiving}
            room={room}
          />
          {showConnectionAlert && <ConnectionAlert />}
        </>
      );
    }

    const me = room.players.find((player) => player.id === credentials.playerId);

    return (
      <main className="shell">
        <section className="card room">
          <RoomInviteQr roomCode={room.code} />
          <h2>Players</h2>
          <ol className="players">
            {room.players.map((player) => (
              <li key={player.id}>
                <span className={player.isConnected ? 'online' : 'offline'} />
                <span className="player-name">
                  <span>{player.nickname}{player.id === me?.id ? ' (you)' : ''}</span>
                  {player.id === me?.id && (
                    <button
                      aria-label="Change my nickname"
                      className="nickname-edit-button"
                      onClick={changeNickname}
                      title="Change my nickname"
                      type="button"
                    >
                      <span aria-hidden="true">&#9998;</span>
                    </button>
                  )}
                </span>
                {player.isHost && <strong>Host</strong>}
              </li>
            ))}
          </ol>
          {room.phase === 'freestyle' || me?.isHost ? (
            <button
              className="primary"
              disabled={busy}
              onClick={openBoard}
            >
              {room.phase === 'freestyle' ? 'Return to board' : 'Open board'}
            </button>
          ) : (
            <p className="muted">Waiting for the host to start.</p>
          )}
          <button
            className="room-leave-button"
            disabled={busy}
            onClick={leaveRoom}
            type="button"
          >
            Leave room
          </button>
          {error && <p className="error">{error}</p>}
        </section>
        {showConnectionAlert && <ConnectionAlert />}
      </main>
    );
  }

  return (
    <main
      className="shell landing-shell"
      style={viewportHeight === null ? undefined : { height: viewportHeight }}
    >
      <section className="card">
        <h1>Concept</h1>
        <p className="intro">Create a room, then have everyone join on their own device.</p>
        <div className="tabs">
          <button className={mode === 'create' ? 'active' : ''} onClick={() => setMode('create')}>
            Create
          </button>
          <button className={mode === 'join' ? 'active' : ''} onClick={() => setMode('join')}>
            Join
          </button>
        </div>
        <form onSubmit={submit}>
          <label>
            Nickname
            <input
              autoComplete="nickname"
              maxLength={20}
              onChange={(event) => setNickname(event.target.value)}
              required
              value={nickname}
            />
          </label>
          {mode === 'join' && (
            <>
              <div className="join-code-controls">
                <label>
                  Room code
                  <input
                    autoCapitalize="characters"
                    maxLength={4}
                    onChange={(event) => setRoomCode(event.target.value.toUpperCase())}
                    pattern="[23456789A-HJ-KM-NP-Z]{4}"
                    required
                    value={roomCode}
                  />
                </label>
                <button className="scan-qr-button" onClick={() => setShowQrScanner(true)} type="button">
                  <span aria-hidden="true">⌗</span>
                  Scan QR
                </button>
              </div>
              <div className="join-code-divider"><span>or join a public game</span></div>
              <section aria-label="Public games" className="public-game-list">
                <strong>Public games</strong>
                {publicRooms.length > 0 ? (
                  <div>
                    {publicRooms.map((publicRoom) => (
                      <button
                        aria-pressed={roomCode === publicRoom.roomCode}
                        className={roomCode === publicRoom.roomCode ? 'selected' : ''}
                        disabled={publicRoom.isFull}
                        key={publicRoom.roomCode}
                        onClick={() => setRoomCode(publicRoom.roomCode)}
                        type="button"
                      >
                        <span>{publicRoom.name}</span>
                        <small>
                          {publicRoom.isFull
                            ? 'Full'
                            : `${publicRoom.playerCount} ${publicRoom.playerCount === 1 ? 'player' : 'players'}`}
                        </small>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p>No public games are available.</p>
                )}
              </section>
            </>
          )}
          {mode === 'create' && (
            <label className="public-game-option">
              <input
                checked={isPublic}
                onChange={(event) => setIsPublic(event.target.checked)}
                type="checkbox"
              />
              <span>Public game</span>
            </label>
          )}
          <button className="primary" disabled={busy} type="submit">
            {busy ? 'Working...' : mode === 'create' ? 'Create room' : 'Join room'}
          </button>
          {error && <p className="error">{error}</p>}
        </form>
        <InstallApp />
        {process.env.NODE_ENV === 'development' && (
          <Link className="temporary-board-preview-link" href="/board-preview">
            <small>Temporary</small>
            Preview board
          </Link>
        )}
      </section>
      {showQrScanner && (
        <QrJoinScanner
          onClose={() => setShowQrScanner(false)}
          onRoomCode={acceptScannedRoomCode}
        />
      )}
      {showConnectionAlert && <ConnectionAlert />}
    </main>
  );
}

function ConnectionAlert() {
  return (
    <aside aria-live="assertive" className="connection-alert" role="alert">
      <strong>Connection unavailable</strong>
      <span>We can&apos;t reach the game server. We&apos;ll keep trying automatically.</span>
    </aside>
  );
}

function readSavedSession(): string | null {
  try {
    return window.localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

function readSavedNickname(): string {
  try {
    return window.localStorage.getItem(NICKNAME_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

function saveNickname(nickname: string) {
  const normalizedNickname = nickname.trim();
  if (!normalizedNickname) return;
  try {
    window.localStorage.setItem(NICKNAME_KEY, normalizedNickname);
  } catch {
    // Remembering a nickname is optional when browser storage is unavailable.
  }
}

function saveSession(credentials: SessionCredentials) {
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(credentials));
  } catch {
    // Safari privacy settings can disable storage; the live session still works.
  }
}

function clearSavedSession() {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // Treat unavailable storage like an empty session.
  }
}
